import api from "./httpClient";
import { API_PATHS } from "./apiPaths";
import { ADMIN_ROLES, normalizeRole } from "../utils/authRole";

const SUBSCRIPTION_PLAN_ENUMS = new Set([
  "ONE_MONTH",
  "THREE_MONTH",
  "SIX_MONTH",
  "ONE_YEAR",
]);

const normalizeOfferCode = (value = "") => String(value)
  .toUpperCase()
  .replace(/[^A-Z0-9_-]/g, "")
  .slice(0, 20);

export const initiateSubscriptionPayment = async ({ plan, couponCode, referralCode } = {}) => {
  const safePlan = typeof plan === "string" ? plan.trim().toUpperCase() : "";
  const normalizedPlan = SUBSCRIPTION_PLAN_ENUMS.has(safePlan) ? safePlan : "";
  const safeCouponCode = normalizeOfferCode(couponCode);
  const safeReferralCode = normalizeOfferCode(referralCode);
  const payUrl = normalizedPlan
    ? `${API_PATHS.subscription.pay}?plan=${encodeURIComponent(normalizedPlan)}`
    : API_PATHS.subscription.pay;
  const payload = {};
  if (safeCouponCode) payload.couponCode = safeCouponCode;
  if (safeReferralCode) payload.referralCode = safeReferralCode;
  const resp = await api.post(payUrl, payload);
  const data = resp?.data?.data ?? resp?.data ?? resp ?? {};
  // Backend wraps the order inside an "amount" key: { amount: { orderId, key, amount } }
  return data?.amount && typeof data.amount === "object" ? data.amount : data;
};

// GET /subscription/status → { plan, expired, expiryDate, active }
export const getSubscriptionStatus = async () => {
  const resp = await api.get(API_PATHS.subscription.status);
  return resp?.data?.data ?? resp?.data ?? null;
};

export const getSubscriptionPlans = async () => {
  const resp = await api.get(API_PATHS.subscription.plans);
  const data = resp?.data?.data ?? resp?.data ?? [];
  return Array.isArray(data) ? data : data?.plans || data?.items || [];
};

// POST /subscription/request-extension { reason }
export const requestExtension = async ({ reason = "Need more time to evaluate" } = {}) => {
  const resp = await api.post(API_PATHS.subscription.requestExtension, { reason });
  return resp?.data?.data ?? resp?.data ?? resp ?? {};
};

const getCurrentUserRole = () => {
  try {
    const saved = localStorage.getItem("user");
    if (!saved) return "";
    let parsed = saved;
    try {
      parsed = JSON.parse(saved);
    } catch {
      // Login stores the normalized role as a plain string.
    }
    const raw = parsed?.role ?? parsed;
    return normalizeRole(typeof raw === "string" ? raw : String(raw));
  } catch {
    return "";
  }
};

// POST /subscription/cancel/request?reason=
export const requestCancellation = async ({ reason = "Need to discontinue subscription" } = {}) => {
  const currentRole = getCurrentUserRole();
  if (!ADMIN_ROLES.includes(currentRole)) {
    throw new Error("Only Admin or Super Admin can request cancellation.");
  }

  const safeReason = (reason || "Need to discontinue subscription").trim();
  const cancelUrl = `${API_PATHS.subscription.requestCancellation}?reason=${encodeURIComponent(safeReason)}`;
  const resp = await api.post(cancelUrl, {});
  return resp?.data?.data ?? resp?.data ?? resp ?? {};
};

// GET /subscription/cancellation-requests
export const getCancellationRequests = async () => {
  const resp = await api.get(API_PATHS.superAdmin.cancellationRequests());
  const data = resp?.data?.data ?? resp?.data ?? [];
  return Array.isArray(data) ? data : [];
};

// POST /subscription/cancel/approve?requestId=
export const approveCancellationRequest = async (requestId) => {
  const resp = await api.post(API_PATHS.superAdmin.approveCancellationRequest(requestId), {});
  return resp?.data?.data ?? resp?.data ?? resp ?? {};
};

// POST /subscription/cancel/reject?requestId=
export const rejectCancellationRequest = async (requestId) => {
  const resp = await api.post(API_PATHS.superAdmin.rejectCancellationRequest(requestId), {});
  return resp?.data?.data ?? resp?.data ?? resp ?? {};
};

// POST /subscription/cancel/direct?tenantId=
export const cancelSubscriptionDirect = async (tenantId) => {
  const resp = await api.post(API_PATHS.superAdmin.cancelDirect(tenantId), {});
  return resp?.data?.data ?? resp?.data ?? resp ?? {};
};

// GET /subscription/payments
export const getPaymentHistory = async () => {
  try {
    const resp = await api.get(API_PATHS.subscription.payments);
    const data = resp?.data?.data ?? resp?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

// ── Admission / other payments (unchanged) ────────────────────────────────

export const createOrder = async ({ amount, label = "Admission", tenantId } = {}) => {
  if (!amount || Number(amount) <= 0) throw new Error("Invalid payment amount.");
  const resp = await api.post(API_PATHS.payments.createOrder, { amount: Number(amount), label, tenantId });
  const data = resp?.data?.data ?? resp?.data ?? resp ?? {};
  if (!data?.orderId && !data?.order_id && !data?.id) throw new Error("Server did not return a valid order. Please try again.");
  return {
    orderId: data.orderId || data.order_id || data.id,
    keyId: data.keyId || data.key_id || data.razorpayKeyId || "",
    amount: data.amount || amount,
    currency: data.currency || "INR",
  };
};

export const verifyPayment = async (razorpayResponse) => {
  // Normalize payload from Razorpay checkout variants.
  const payload = {
    razorpay_order_id:
      razorpayResponse?.razorpay_order_id
      || razorpayResponse?.razorpayOrderId
      || razorpayResponse?.order_id
      || razorpayResponse?.orderId
      || razorpayResponse?.response?.razorpay_order_id
      || "",
    razorpay_payment_id:
      razorpayResponse?.razorpay_payment_id
      || razorpayResponse?.razorpayPaymentId
      || razorpayResponse?.payment_id
      || razorpayResponse?.paymentId
      || razorpayResponse?.response?.razorpay_payment_id
      || "",
    razorpay_signature:
      razorpayResponse?.razorpay_signature
      || razorpayResponse?.razorpaySignature
      || razorpayResponse?.signature
      || razorpayResponse?.response?.razorpay_signature
      || "",
  };

  if (!payload.razorpay_order_id || !payload.razorpay_payment_id || !payload.razorpay_signature) {
    throw new Error("Incomplete payment response from Razorpay gateway.");
  }

  // Include multiple key variants because backend DTOs vary across services.
  const requestBody = {
    ...payload,
    orderId: payload.razorpay_order_id,
    paymentId: payload.razorpay_payment_id,
    signature: payload.razorpay_signature,
    order_id: payload.razorpay_order_id,
    payment_id: payload.razorpay_payment_id,
  };

  if (process.env.NODE_ENV !== "production") {
    console.debug("[payments.verifyPayment] endpoint/body", API_PATHS.payments.verifyPayment, {
      ...requestBody,
      razorpay_signature: requestBody.razorpay_signature ? "***" : "",
      signature: requestBody.signature ? "***" : "",
    });
  }

  const resp = await api.post(API_PATHS.payments.verifyPayment, requestBody);
  const data = resp?.data?.data ?? resp?.data ?? resp ?? {};
  if (data?.verified === false || data?.success === false) throw new Error(data?.message || "Payment verification failed on server.");
  return data;
};
